import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'

interface EmptyStateAction {
  label: string
  onClick: () => void
}

export interface EmptyStateProps {
  /** A lucide icon that previews the kind of thing that will fill this space. */
  icon: LucideIcon
  /** One short, inviting line — what the person can start here. */
  title: string
  /** One sentence of orientation. Optional. */
  description?: string
  /** The primary thing to do — usually opens the "add" flow for this screen. */
  action?: EmptyStateAction
  /** A quieter alternative, e.g. "Clear filters" on a filtered-to-nothing list. */
  secondaryAction?: EmptyStateAction
  /** Tighter spacing for empty states that sit inside a card or a list section. */
  compact?: boolean
  className?: string
}

// A populated screen replaces this with rows of real data; the empty state rehearses
// that shape — the same tinted icon tile the filled UI uses — so it reads as "your
// accounts will live here", not a dead end. See the app-wide empty-state pass.
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  secondaryAction,
  compact = false,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center text-center motion-safe:animate-fade-slide-up',
        compact ? 'px-4 py-10' : 'px-6 py-16',
        className
      )}
    >
      <span
        className={cn(
          'flex items-center justify-center rounded-xl bg-[var(--fl-primary-subtle)] text-[var(--fl-primary-text)] ring-1 ring-inset ring-[var(--fl-primary)]/10',
          compact ? 'h-9 w-9' : 'h-12 w-12'
        )}
      >
        <Icon size={compact ? 16 : 22} strokeWidth={1.75} />
      </span>

      <p
        className={cn(
          'mt-4 font-serif font-semibold text-[var(--fl-heading)]',
          compact ? 'text-sm' : 'text-[17px]'
        )}
      >
        {title}
      </p>

      {description && (
        <p className="mt-1.5 max-w-sm text-sm leading-relaxed text-[var(--fl-text-2)]">
          {description}
        </p>
      )}

      {(action || secondaryAction) && (
        <div className="mt-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
          {action && (
            <Button size="sm" onClick={action.onClick}>
              {action.label}
            </Button>
          )}
          {secondaryAction && (
            <Button
              variant="link"
              size="sm"
              onClick={secondaryAction.onClick}
              className="h-auto p-0"
            >
              {secondaryAction.label}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
